import { useCallback } from 'react';

import { gql } from '@apollo/client';
import { useMutation, useQuery } from '@apollo/client/react';
import type {
  AgentConversation,
  AgentMessage,
  AgentRun,
  DoBoxExecutorFeature,
  ExecutorActionResultStatus,
  ExecutorItemRef,
  ExecutorMode,
} from '@this+that/models';
import log from 'loglevel';

import { ReplyWaitCancelFailure } from './replyWaitCancelFailure';

/** DoBoxExecutorAccess — mode plus the executor features it also enables.
 *  Not a libs/models type: it is resolved server-side only and the web only
 *  ever sees this GraphQL shape. */
export interface DoBoxExecutorAccess {
  mode: ExecutorMode;
  features: DoBoxExecutorFeature[];
}

/** DoBoxExecutorSession — the caller's resolved access plus the conversation
 *  it is linked to. `conversation` is null until the first send creates it
 *  via openDoBoxExecutorSession. */
export interface DoBoxExecutorSession {
  mode: ExecutorMode;
  features: DoBoxExecutorFeature[];
  conversation: AgentConversation | null;
  legacyConversationId: string | null;
}

export type ExecutorActionRun = Pick<
  AgentRun,
  'runId' | 'conversationId' | 'status'
>;

export interface ExecutorActionResult {
  run: ExecutorActionRun | null;
  alreadyExecuted: boolean;
  status: ExecutorActionResultStatus;
}

/** Mirrors the GraphQL ExecutorUndoFailure enum — not shared via libs/models
 *  or libs/constants, so this stays a local string union here. */
export type ExecutorUndoFailure =
  | 'TOO_LATE'
  | 'PROVIDER_FAILED'
  | 'NOT_UNDOABLE';

export interface ExecutorUndoResult {
  undone: boolean;
  reason: ExecutorUndoFailure | null;
}

/** Builds the (conversationId, runId, msgIdx) ref every executor item
 *  mutation takes, straight off the AgentMessage the card renders — never
 *  constructed by hand at a call site. */
export function buildExecutorItemRef(
  message: Pick<AgentMessage, 'conversationId' | 'runId' | 'msgIdx'>,
): ExecutorItemRef {
  return {
    conversationId: message.conversationId,
    runId: message.runId,
    msgIdx: message.msgIdx,
  };
}

export const MY_DOBOX_EXECUTOR_FEATURES = gql`
  query myDoBoxExecutorFeatures {
    myDoBoxExecutorFeatures {
      mode
      features
    }
  }
`;

interface MyDoBoxExecutorFeaturesResult {
  myDoBoxExecutorFeatures: DoBoxExecutorAccess;
}

export interface UseMyDoBoxExecutorFeaturesOptions {
  skip?: boolean;
}

/** Raw query hook — fetchPolicy is cache-and-network so a stale cached
 *  access never blocks a fresher server read. useDoBoxExecutorFlags
 *  (hooks/DoBox) is the fail-closed consumer; components use that, not this. */
export function useMyDoBoxExecutorFeatures(
  options?: UseMyDoBoxExecutorFeaturesOptions,
) {
  const { data, loading, error, refetch } =
    useQuery<MyDoBoxExecutorFeaturesResult>(MY_DOBOX_EXECUTOR_FEATURES, {
      fetchPolicy: 'cache-and-network',
      skip: options?.skip,
    });

  return {
    access: data?.myDoBoxExecutorFeatures ?? null,
    loading,
    error: error ?? null,
    refetch,
  };
}

const DOBOX_EXECUTOR_SESSION_CONVERSATION_FIELDS = `
  conversationId
  userId
  title
  createdAt
  lastMessageAt
  messageCount
  lastRunId
  archivedAt
  isSystem
  systemConversationKind
  pendingUserAction
  conversationKind
  sourceInboxEntryRef {
    taskGroupId
    entryId
  }
`;

export const DOBOX_EXECUTOR_SESSION = gql`
  query doBoxExecutorSession($taskGroupId: ID!, $entryId: ID!) {
    doBoxExecutorSession(taskGroupId: $taskGroupId, entryId: $entryId) {
      mode
      features
      legacyConversationId
      conversation {
        ${DOBOX_EXECUTOR_SESSION_CONVERSATION_FIELDS}
      }
    }
  }
`;

interface DoBoxExecutorSessionResult {
  doBoxExecutorSession: DoBoxExecutorSession;
}

interface DoBoxExecutorSessionVariables {
  taskGroupId: string;
  entryId: string;
}

export interface UseDoBoxExecutorSessionOptions {
  skip?: boolean;
}

/** Read-only: never creates the conversation — openDoBoxExecutorSession
 *  below does. */
export function useDoBoxExecutorSession(
  taskGroupId: string,
  entryId: string,
  options?: UseDoBoxExecutorSessionOptions,
) {
  const { data, loading, error, refetch } =
    useQuery<DoBoxExecutorSessionResult>(DOBOX_EXECUTOR_SESSION, {
      variables: {
        taskGroupId,
        entryId,
      } satisfies DoBoxExecutorSessionVariables,
      skip: options?.skip,
      fetchPolicy: 'network-only',
    });

  return {
    session: data?.doBoxExecutorSession ?? null,
    loading,
    error: error ?? null,
    refetch,
  };
}

export const OPEN_DOBOX_EXECUTOR_SESSION = gql`
  mutation openDoBoxExecutorSession($taskGroupId: ID!, $entryId: ID!) {
    openDoBoxExecutorSession(taskGroupId: $taskGroupId, entryId: $entryId) {
      mode
      features
      legacyConversationId
      conversation {
        ${DOBOX_EXECUTOR_SESSION_CONVERSATION_FIELDS}
      }
    }
  }
`;

interface OpenDoBoxExecutorSessionResult {
  openDoBoxExecutorSession: DoBoxExecutorSession;
}

export function useOpenDoBoxExecutorSession() {
  const [mutate, { loading, error }] =
    useMutation<OpenDoBoxExecutorSessionResult>(OPEN_DOBOX_EXECUTOR_SESSION);

  const openDoBoxExecutorSession = useCallback(
    async (taskGroupId: string, entryId: string) => {
      const { data } = await mutate({
        variables: { taskGroupId, entryId },
      });
      return data?.openDoBoxExecutorSession ?? null;
    },
    [mutate],
  );

  return { openDoBoxExecutorSession, loading, error };
}

const EXECUTOR_ACTION_RESULT_FIELDS = `
  alreadyExecuted
  status
  run {
    runId
    conversationId
    status
  }
`;

export const APPROVE_EXECUTOR_PROPOSAL = gql`
  mutation approveExecutorProposal(
    $ref: ExecutorItemRefInput!
    $editedBodyText: String
    $selectedSubItemIds: [ID!]
  ) {
    approveExecutorProposal(
      ref: $ref
      editedBodyText: $editedBodyText
      selectedSubItemIds: $selectedSubItemIds
    ) {
      ${EXECUTOR_ACTION_RESULT_FIELDS}
    }
  }
`;

interface ApproveExecutorProposalResult {
  approveExecutorProposal: ExecutorActionResult;
}

export function useApproveExecutorProposal() {
  const [mutate, { loading, error }] =
    useMutation<ApproveExecutorProposalResult>(APPROVE_EXECUTOR_PROPOSAL);

  const approveExecutorProposal = useCallback(
    async (
      ref: ExecutorItemRef,
      editedBodyText?: string,
      selectedSubItemIds?: string[],
    ) => {
      const { data } = await mutate({
        variables: { ref, editedBodyText, selectedSubItemIds },
      });
      return data?.approveExecutorProposal ?? null;
    },
    [mutate],
  );

  return { approveExecutorProposal, loading, error };
}

export const ANSWER_EXECUTOR_QUESTION = gql`
  mutation answerExecutorQuestion(
    $ref: ExecutorItemRefInput!
    $optionIndex: Int
    $text: String
  ) {
    answerExecutorQuestion(ref: $ref, optionIndex: $optionIndex, text: $text) {
      ${EXECUTOR_ACTION_RESULT_FIELDS}
    }
  }
`;

interface AnswerExecutorQuestionResult {
  answerExecutorQuestion: ExecutorActionResult;
}

export function useAnswerExecutorQuestion() {
  const [mutate, { loading, error }] =
    useMutation<AnswerExecutorQuestionResult>(ANSWER_EXECUTOR_QUESTION);

  const answerExecutorQuestion = useCallback(
    async (ref: ExecutorItemRef, optionIndex?: number, text?: string) => {
      const { data } = await mutate({
        variables: { ref, optionIndex, text },
      });
      return data?.answerExecutorQuestion ?? null;
    },
    [mutate],
  );

  return { answerExecutorQuestion, loading, error };
}

export const DISMISS_EXECUTOR_ITEM = gql`
  mutation dismissExecutorItem($ref: ExecutorItemRefInput!) {
    dismissExecutorItem(ref: $ref) {
      ${EXECUTOR_ACTION_RESULT_FIELDS}
    }
  }
`;

interface DismissExecutorItemResult {
  dismissExecutorItem: ExecutorActionResult;
}

export function useDismissExecutorItem() {
  const [mutate, { loading, error }] = useMutation<DismissExecutorItemResult>(
    DISMISS_EXECUTOR_ITEM,
  );

  const dismissExecutorItem = useCallback(
    async (ref: ExecutorItemRef) => {
      const { data } = await mutate({ variables: { ref } });
      return data?.dismissExecutorItem ?? null;
    },
    [mutate],
  );

  return { dismissExecutorItem, loading, error };
}

export const RETRY_EXECUTOR_ITEM = gql`
  mutation retryExecutorItem($ref: ExecutorItemRefInput!) {
    retryExecutorItem(ref: $ref) {
      ${EXECUTOR_ACTION_RESULT_FIELDS}
    }
  }
`;

interface RetryExecutorItemResult {
  retryExecutorItem: ExecutorActionResult;
}

export function useRetryExecutorItem() {
  const [mutate, { loading, error }] =
    useMutation<RetryExecutorItemResult>(RETRY_EXECUTOR_ITEM);

  const retryExecutorItem = useCallback(
    async (ref: ExecutorItemRef) => {
      const { data } = await mutate({ variables: { ref } });
      return data?.retryExecutorItem ?? null;
    },
    [mutate],
  );

  return { retryExecutorItem, loading, error };
}

export const UNDO_EXECUTOR_ACTION = gql`
  mutation undoExecutorAction($ref: ExecutorItemRefInput!) {
    undoExecutorAction(ref: $ref) {
      undone
      reason
    }
  }
`;

interface UndoExecutorActionResult {
  undoExecutorAction: ExecutorUndoResult;
}

export function useUndoExecutorAction() {
  const [mutate, { loading, error }] =
    useMutation<UndoExecutorActionResult>(UNDO_EXECUTOR_ACTION);

  const undoExecutorAction = useCallback(
    async (ref: ExecutorItemRef) => {
      const { data } = await mutate({ variables: { ref } });
      return data?.undoExecutorAction ?? null;
    },
    [mutate],
  );

  return { undoExecutorAction, loading, error };
}

export { ReplyWaitCancelFailure };

export interface ExecutorCancelReplyWaitResult {
  cancelled: boolean;
  reason?: ReplyWaitCancelFailure | null;
}

export const CANCEL_EXECUTOR_REPLY_WAIT = gql`
  mutation cancelExecutorReplyWait($ref: ExecutorItemRefInput!) {
    cancelExecutorReplyWait(ref: $ref) {
      cancelled
      reason
    }
  }
`;

interface CancelExecutorReplyWaitData {
  cancelExecutorReplyWait: ExecutorCancelReplyWaitResult;
}

export function useCancelExecutorReplyWait() {
  const [mutate, { loading, error }] = useMutation<CancelExecutorReplyWaitData>(
    CANCEL_EXECUTOR_REPLY_WAIT,
  );

  const cancelExecutorReplyWait = useCallback(
    async (ref: ExecutorItemRef) => {
      const { data } = await mutate({ variables: { ref } });
      return data?.cancelExecutorReplyWait ?? null;
    },
    [mutate],
  );

  return { cancelExecutorReplyWait, loading, error };
}

/**
 * CONTRACT-ONLY: the executeExecutorNextStep API mutation lands in ADD-NEXT.A
 * (wave 26) — it does not exist on the API yet. This document and hook ship
 * ahead of it so the web deploys once, after the whole API PR; the button
 * that calls it only renders once ADD-NEXT.W starts writing next steps.
 */
export const EXECUTE_EXECUTOR_NEXT_STEP = gql`
  mutation executeExecutorNextStep($ref: ExecutorItemRefInput!, $nextStepId: String!) {
    executeExecutorNextStep(ref: $ref, nextStepId: $nextStepId) {
      ${EXECUTOR_ACTION_RESULT_FIELDS}
    }
  }
`;

interface ExecuteExecutorNextStepResult {
  executeExecutorNextStep: ExecutorActionResult;
}

export function useExecuteExecutorNextStep() {
  const [mutate, { loading, error }] =
    useMutation<ExecuteExecutorNextStepResult>(EXECUTE_EXECUTOR_NEXT_STEP);

  const executeExecutorNextStep = useCallback(
    async (ref: ExecutorItemRef, nextStepId: string) => {
      const { data } = await mutate({ variables: { ref, nextStepId } });
      return data?.executeExecutorNextStep ?? null;
    },
    [mutate],
  );

  return { executeExecutorNextStep, loading, error };
}

/** Ticket return path for armDoBoxCapabilityResume (WEB-X-CAP.01.b/API-CAP.03)
 *  — saved before the web navigates away for a connection step, so
 *  WEB-X-CAP.02's auto-resume knows where to bring the user back. */
export interface DoBoxCapabilityResumeArm {
  returnPath: string;
}

export const ARM_DOBOX_CAPABILITY_RESUME = gql`
  mutation armDoBoxCapabilityResume($ref: ExecutorItemRefInput!) {
    armDoBoxCapabilityResume(ref: $ref) {
      returnPath
    }
  }
`;

interface ArmDoBoxCapabilityResumeResult {
  armDoBoxCapabilityResume: DoBoxCapabilityResumeArm;
}

export function useArmDoBoxCapabilityResume() {
  const [mutate, { loading, error }] =
    useMutation<ArmDoBoxCapabilityResumeResult>(ARM_DOBOX_CAPABILITY_RESUME);

  const armDoBoxCapabilityResume = useCallback(
    async (ref: ExecutorItemRef) => {
      const { data } = await mutate({ variables: { ref } });
      return data?.armDoBoxCapabilityResume ?? null;
    },
    [mutate],
  );

  return { armDoBoxCapabilityResume, loading, error };
}

/** Mirrors CapabilityRemedyView (doboxExecutorSchema.ts) — capabilityKey,
 *  remedy and alternatives hold libs/constants enum values, kept as string
 *  here the same way ExecutorCapabilityGap does (libs/models). */
export interface DoBoxCapabilityRemedyView {
  capabilityKey: string;
  remedy: string;
  connectionId: string | null;
  toolName: string | null;
  alternatives: string[];
}

/** Mirrors CapabilityResumeResult — the shared result shape of
 *  resumeDoBoxExecutor and enableMcpToolForDoBox. `remedy` is set only when
 *  the need is still missing; the web renders the final outcome from the
 *  executor conversation's own ledger rows, never from this result alone. */
export interface DoBoxCapabilityResumeOutcome {
  resumed: boolean;
  exhausted: boolean;
  remedy: DoBoxCapabilityRemedyView | null;
  alternatives: string[];
  run: ExecutorActionRun | null;
}

const CAPABILITY_RESUME_OUTCOME_FIELDS = `
  resumed
  exhausted
  alternatives
  remedy {
    capabilityKey
    remedy
    connectionId
    toolName
    alternatives
  }
  run {
    runId
    conversationId
    status
  }
`;

export const RESUME_DOBOX_EXECUTOR = gql`
  mutation resumeDoBoxExecutor(
    $taskGroupId: ID!
    $entryId: ID!
    $ref: ExecutorItemRefInput
  ) {
    resumeDoBoxExecutor(taskGroupId: $taskGroupId, entryId: $entryId, ref: $ref) {
      ${CAPABILITY_RESUME_OUTCOME_FIELDS}
    }
  }
`;

interface ResumeDoBoxExecutorResult {
  resumeDoBoxExecutor: DoBoxCapabilityResumeOutcome;
}

export function useResumeDoBoxExecutor() {
  const [mutate, { loading, error }] = useMutation<ResumeDoBoxExecutorResult>(
    RESUME_DOBOX_EXECUTOR,
  );

  const resumeDoBoxExecutor = useCallback(
    async (taskGroupId: string, entryId: string, ref?: ExecutorItemRef) => {
      const { data } = await mutate({
        variables: { taskGroupId, entryId, ref },
      });
      return data?.resumeDoBoxExecutor ?? null;
    },
    [mutate],
  );

  return { resumeDoBoxExecutor, loading, error };
}

export const ENABLE_MCP_TOOL_FOR_DOBOX = gql`
  mutation enableMcpToolForDoBox(
    $ref: ExecutorItemRefInput!
    $connectionId: ID!
    $toolName: String!
  ) {
    enableMcpToolForDoBox(
      ref: $ref
      connectionId: $connectionId
      toolName: $toolName
    ) {
      ${CAPABILITY_RESUME_OUTCOME_FIELDS}
    }
  }
`;

interface EnableMcpToolForDoBoxResult {
  enableMcpToolForDoBox: DoBoxCapabilityResumeOutcome;
}

export function useEnableMcpToolForDoBox() {
  const [mutate, { loading, error }] = useMutation<EnableMcpToolForDoBoxResult>(
    ENABLE_MCP_TOOL_FOR_DOBOX,
  );

  const enableMcpToolForDoBox = useCallback(
    async (ref: ExecutorItemRef, connectionId: string, toolName: string) => {
      const { data } = await mutate({
        variables: { ref, connectionId, toolName },
      });
      return data?.enableMcpToolForDoBox ?? null;
    },
    [mutate],
  );

  return { enableMcpToolForDoBox, loading, error };
}

/** Mirrors DoBoxRef (agentSchema.ts) — the 'N esperando X · Continuar'
 *  aggregate's rows. Each row's own resume is still a separate
 *  resumeDoBoxExecutor call; this query only lists them. */
export interface DoBoxCapabilityWaitingRef {
  taskGroupId: string;
  entryId: string;
}

export const DOBOXES_WAITING_ON_CAPABILITY = gql`
  query doBoxesWaitingOnCapability($capabilityKey: CapabilityKey!) {
    doBoxesWaitingOnCapability(capabilityKey: $capabilityKey) {
      taskGroupId
      entryId
    }
  }
`;

interface DoBoxesWaitingOnCapabilityResult {
  doBoxesWaitingOnCapability: DoBoxCapabilityWaitingRef[];
}

/** Imperative fetch, mirroring useGetIntegrationRequestsReport
 *  (integrationRequest.service.ts) — the "N others waiting" row fetches this
 *  only on click, never eagerly on every capability card render. */
export function useDoBoxesWaitingOnCapability() {
  const { loading, refetch } = useQuery<DoBoxesWaitingOnCapabilityResult>(
    DOBOXES_WAITING_ON_CAPABILITY,
    { skip: true },
  );

  const fetchDoBoxesWaitingOnCapability = useCallback(
    async (capabilityKey: string) => {
      try {
        const { data } = await refetch({ capabilityKey });
        return data?.doBoxesWaitingOnCapability ?? [];
      } catch (error) {
        log.error('[doBoxExecutor.service] doBoxesWaitingOnCapability failed', {
          capabilityKey,
          error,
        });
        return [];
      }
    },
    [refetch],
  );

  return { fetchDoBoxesWaitingOnCapability, loading };
}
